import React, { useState } from 'react'

import {
    Button,
    CircularProgress,
    IconButton,
    Switch,
    Tab,
    Tabs,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Add, Delete } from '@material-ui/icons'

import {
    CellData,
    Container,
    HeadCell,
    RenderRow,
    RowData,
    SimpleTable,
} from '../../components'
import { Concession, Premium } from '../../models/Concession'
import { useConcessions } from '../../hooks/useConcessions'
import { usePremiums } from '../../hooks/usePremiums'
import { ConcessionModal } from './ConcessionModal'
import { PremiumModal } from './PremiumModal'
import { displayApiDate, displayLongDate } from './utils'

interface ConcessionRowType extends RowData {
    name: CellData<string>
    status: CellData<JSX.Element>
    startDate: CellData<string>
    duration: CellData<string>
    endDate: CellData<string>
    actions: CellData<JSX.Element>
    concession: CellData<Concession>
}

interface PremiumRowType extends RowData {
    name: CellData<string>
    amount: CellData<string>
    status: CellData<JSX.Element>
    actions: CellData<JSX.Element>
    premium: CellData<Premium>
}

export const ConcessionsHome: React.FC = () => {
    const theme = useTheme()
    const {
        concessions,
        isLoading,
        isApartment,
        toggleActive,
        deleteConcession,
    } = useConcessions()
    const {
        premiums,
        isLoading: premiumsLoading,
        toggleActive: togglePremiumActive,
        deletePremium,
    } = usePremiums()

    const [tab, setTab] = useState(0)
    const [modalOpen, setModalOpen] = useState(false)
    const [selected, setSelected] = useState<Concession | undefined>()
    const [premiumModalOpen, setPremiumModalOpen] = useState(false)
    const [selectedPremium, setSelectedPremium] = useState<
        Premium | undefined
    >()

    const openNew = () => {
        setSelected(undefined)
        setModalOpen(true)
    }

    const openEdit = (concession: Concession) => {
        setSelected(concession)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setSelected(undefined)
    }

    const openNewPremium = () => {
        setSelectedPremium(undefined)
        setPremiumModalOpen(true)
    }

    const openEditPremium = (premium: Premium) => {
        setSelectedPremium(premium)
        setPremiumModalOpen(true)
    }

    const closePremiumModal = () => {
        setPremiumModalOpen(false)
        setSelectedPremium(undefined)
    }

    const headCells: HeadCell<ConcessionRowType>[] = [
        { id: 'name', label: 'Name', align: 'left' },
        { id: 'status', label: 'Status', align: 'left', sortable: false },
        { id: 'startDate', label: 'Start Date', align: 'left' },
        { id: 'duration', label: 'Duration', align: 'left' },
        { id: 'endDate', label: 'Ends', align: 'left' },
        { id: 'actions', label: '', align: 'right', sortable: false },
    ]

    const createRow = (concession: Concession): ConcessionRowType => {
        const statusSwitch = (
            <Tooltip
                title={
                    concession.is_active
                        ? 'Active — visible to tenants'
                        : 'Inactive — retired'
                }
            >
                <Switch
                    checked={concession.is_active}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        e.stopPropagation()
                        toggleActive.mutate({
                            id: concession.id,
                            isActive: e.target.checked,
                        })
                    }}
                />
            </Tooltip>
        )

        const actions = (
            <Tooltip title="Delete concession">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation()
                        if (
                            window.confirm(
                                `Delete concession "${concession.name}"? This cannot be undone.`,
                            )
                        ) {
                            deleteConcession.mutate(concession.id)
                        }
                    }}
                >
                    <Delete fontSize="small" color="error" />
                </IconButton>
            </Tooltip>
        )

        return {
            name: { value: concession.name, sortValue: concession.name },
            status: {
                value: statusSwitch,
                sortValue: concession.is_active ? 1 : 0,
            },
            startDate: {
                value: displayApiDate(concession.start_date),
                sortValue: concession.start_date,
            },
            duration: {
                value: `${concession.duration_days} day${
                    concession.duration_days === 1 ? '' : 's'
                }`,
                sortValue: concession.duration_days,
            },
            endDate: {
                value: displayLongDate(concession.end_date),
                sortValue: concession.end_date,
            },
            actions: { value: actions, sortValue: 0 },
            concession: { value: concession, sortValue: concession.id },
        }
    }

    const ConcessionRow: RenderRow<ConcessionRowType> = (row) => {
        const concession = row.concession.value
        return (
            <TableRow
                key={`CONCESSION_ROW_${concession.id}`}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => openEdit(concession)}
            >
                <TableCell>{row.name.value}</TableCell>
                <TableCell>{row.status.value}</TableCell>
                <TableCell>{row.startDate.value}</TableCell>
                <TableCell>{row.duration.value}</TableCell>
                <TableCell>{row.endDate.value}</TableCell>
                <TableCell align="right">{row.actions.value}</TableCell>
            </TableRow>
        )
    }

    const premiumHeadCells: HeadCell<PremiumRowType>[] = [
        { id: 'name', label: 'Name', align: 'left' },
        { id: 'amount', label: 'Monthly amount', align: 'left' },
        { id: 'status', label: 'Status', align: 'left', sortable: false },
        { id: 'actions', label: '', align: 'right', sortable: false },
    ]

    const createPremiumRow = (premium: Premium): PremiumRowType => {
        const statusSwitch = (
            <Tooltip
                title={
                    premium.is_active
                        ? 'Active — available on lease offers'
                        : 'Inactive — hidden from new offers'
                }
            >
                <Switch
                    checked={premium.is_active}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        e.stopPropagation()
                        togglePremiumActive.mutate({
                            id: premium.id,
                            isActive: e.target.checked,
                        })
                    }}
                />
            </Tooltip>
        )
        const actions = (
            <Tooltip title="Delete premium">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation()
                        if (
                            window.confirm(
                                `Delete premium "${premium.name}"? This cannot be undone.`,
                            )
                        ) {
                            deletePremium.mutate(premium.id)
                        }
                    }}
                >
                    <Delete fontSize="small" color="error" />
                </IconButton>
            </Tooltip>
        )
        return {
            name: { value: premium.name, sortValue: premium.name },
            amount: {
                value: `$${Number(premium.amount).toFixed(2)}`,
                sortValue: Number(premium.amount),
            },
            status: {
                value: statusSwitch,
                sortValue: premium.is_active ? 1 : 0,
            },
            actions: { value: actions, sortValue: 0 },
            premium: { value: premium, sortValue: premium.id },
        }
    }

    const PremiumRow: RenderRow<PremiumRowType> = (row) => {
        const premium = row.premium.value
        return (
            <TableRow
                key={`PREMIUM_ROW_${premium.id}`}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => openEditPremium(premium)}
            >
                <TableCell>{row.name.value}</TableCell>
                <TableCell>{row.amount.value}</TableCell>
                <TableCell>{row.status.value}</TableCell>
                <TableCell align="right">{row.actions.value}</TableCell>
            </TableRow>
        )
    }

    if (!isApartment) {
        return (
            <Container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ height: 'calc(100vh - 140px)', textAlign: 'center' }}
            >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Concessions and premiums are managed per apartment
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: theme.spacing(1), maxWidth: 420 }}
                >
                    Select an apartment workspace from the top bar to create and
                    manage concessions and premiums.
                </Typography>
            </Container>
        )
    }

    return (
        <>
            <Container
                direction="column"
                flex={1}
                style={{ height: 'calc(100vh - 140px)' }}
            >
                <Container
                    alignItems="center"
                    style={{ marginBottom: theme.spacing(2) }}
                >
                    <Container direction="column" flex={1}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            Concessions & Premiums
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Credits and unit upcharges stored as separate
                            charges on lease offers.
                        </Typography>
                    </Container>
                    {tab === 0 ? (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={openNew}
                            style={{ textTransform: 'none' }}
                        >
                            New Concession
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={openNewPremium}
                            style={{ textTransform: 'none' }}
                        >
                            New Premium
                        </Button>
                    )}
                </Container>

                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ marginBottom: theme.spacing(2) }}
                >
                    <Tab
                        label="Concessions"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab label="Premiums" style={{ textTransform: 'none' }} />
                </Tabs>

                {tab === 0 &&
                    (isLoading ? (
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                        >
                            <CircularProgress />
                        </Container>
                    ) : concessions.length === 0 ? (
                        <Container
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                            style={{ textAlign: 'center' }}
                        >
                            <Typography
                                variant="body1"
                                style={{ fontWeight: 600 }}
                            >
                                No concessions yet
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: theme.spacing(1) }}
                            >
                                Create your first renewal concession to get
                                started.
                            </Typography>
                        </Container>
                    ) : (
                        <SimpleTable<ConcessionRowType>
                            rows={concessions.map((c) => createRow(c))}
                            render={(row) => ConcessionRow(row, 0)}
                            orderByDefault="name"
                            headCells={headCells}
                        />
                    ))}

                {tab === 1 &&
                    (premiumsLoading ? (
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                        >
                            <CircularProgress />
                        </Container>
                    ) : premiums.length === 0 ? (
                        <Container
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            flex={1}
                            style={{ textAlign: 'center' }}
                        >
                            <Typography
                                variant="body1"
                                style={{ fontWeight: 600 }}
                            >
                                No premiums yet
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: theme.spacing(1) }}
                            >
                                Create unit upcharges like pool view, top floor,
                                or balcony.
                            </Typography>
                        </Container>
                    ) : (
                        <SimpleTable<PremiumRowType>
                            rows={premiums.map((p) => createPremiumRow(p))}
                            render={(row) => PremiumRow(row, 0)}
                            orderByDefault="name"
                            headCells={premiumHeadCells}
                        />
                    ))}
            </Container>

            <ConcessionModal
                open={modalOpen}
                concession={selected}
                handleClose={closeModal}
            />
            <PremiumModal
                open={premiumModalOpen}
                premium={selectedPremium}
                handleClose={closePremiumModal}
            />
        </>
    )
}
